import type { CommutePlan, DriverProgress, RiderPhase } from "../shared/types";
import type { Direction } from "./sticky";
import type { CardQuestion } from "./rider-journey";
import type { NativeGlyph, NativeJourney, NativeTone } from "../shared/native-bridge";
import { serviceDateKey } from "../shared/time";
import { cardStatus, clock, type CardStatus, type OnwardLeg } from "./format";
import { shortLabel, stationLabel } from "../shared/labels";
import { pickupCard, type PickupTiming } from "./pickup";

export type NativeJourneyPresentation = Pick<NativeJourney, "lead" | "leadShort" | "value" | "detail" | "note" | "tone" | "glyph" | "countdownToMs">;

export interface NativeJourneyInput {
	phase: RiderPhase;
	direction: Direction;
	activityKey?: string;
	reopenActivity?: boolean;
	/**
	 * The decision stays in the web app; the shell only starts or stops CoreLocation as told.
	 *
	 * Optional because the Worker calls this too, to re-render a card it is correcting by push, and
	 * it has no business telling a phone whether to run its GPS. Left out there rather than guessed;
	 * the shell reads an absent value as "keep running", which is the safe way round.
	 */
	locationActive?: boolean;
	/** Pickup has its own timing model, so its already-decided card content bypasses commute modes. */
	presentation?: NativeJourneyPresentation;
	/** Factual GPS-backed copy that replaces an expired onboard ETA. */
	cardOverride?: CardStatus | null;
	mode: CardQuestion;
	plan: CommutePlan | null;
	/** Which stop the onboard card counts to; the ride home has two. */
	destinationIndex: number;
	/** Where he is actually going — "College Prep", a house. */
	finalLabel: string | null;
	/**
	 * The last leg, which no train is involved in.
	 *
	 * Carried because the plan is gone by the time he walks it: his train has arrived, so it has
	 * dropped out of the options and there is nothing left to count down to.
	 */
	onward?: OnwardLeg | null;
	/** The station he is driving to, for the card that is about getting there. */
	boardingLabel: string | null;
	/** Whether there is a drive at all. The ride home starts on a platform. */
	hasAccessLeg: boolean;
	nowMs: number;
	timezone: string;
}

/** Renders the same Pickup decision on Apple surfaces without inventing a commute plan for it. */
export function nativePickupPresentation(input: {
	progress: DriverProgress;
	timing: PickupTiming;
	station: string | null;
	trainArrivalMs: number | null;
	nowMs: number;
	timezone: string;
}): NativeJourneyPresentation {
	const { progress, timing, station, trainArrivalMs, nowMs, timezone } = input;
	const copy = pickupCard(progress, timing, nowMs, trainArrivalMs == null ? null : clock(trainArrivalMs, timezone));
	const stationName = station ? stationLabel(station) : null;
	const drivingTo = progress === "driving" ? timing.driverArrivalMs : null;
	const countdownToMs =
		progress === "not_started" || progress === "preparing"
			? timing.startByMs != null && timing.startByMs > nowMs ? timing.startByMs : null
			: progress === "driving"
				? drivingTo
				: progress === "at_pickup_point" && timing.riderReadyMs != null && timing.riderReadyMs > nowMs
					? timing.riderReadyMs
					: null;
	const drivingLead = drivingTo == null ? null : stationName ? `${stationName.toUpperCase()} in` : "THERE IN";
	const immediate = copy.hero === "LEAVE NOW";
	const lead = drivingLead ?? (immediate ? "LEAVE" : copy.lead ?? copy.hero);
	const tone: NativeTone = copy.tone === "go"
		? timing.deltaSeconds != null && timing.deltaSeconds >= 0 ? "bad" : "warn"
		: copy.tone === "here" || copy.tone === "done" ? "good" : "info";
	return {
		lead,
		leadShort: drivingLead && stationName ? `${shortLabel(stationName)} in` : lead,
		value: immediate ? "NOW" : null,
		detail: copy.sub,
		note: copy.note ? `${copy.note.value} ${copy.note.label}` : null,
		tone,
		glyph: progress === "not_started" || progress === "preparing" ? "leave" : progress === "picked_up" ? "" : "drive",
		countdownToMs,
	};
}

/**
 * Whether native location has a job.
 *
 * Here rather than in the shell because an installed binary cannot be changed without a rebuild,
 * and this rule will. Pickup Mode is a separate claim on the same watch: the driver's position is
 * wanted whatever phase his own journey thinks it is in.
 *
 * The window is in the condition because the phase alone cannot get out of its own way: `planning`
 * is left only by `observablePhase` seeing `withinWindow`, which is computed in the page, and if the
 * radio is already off there are no fixes, no reason for iOS to keep the app alive, and nothing left
 * to say "start again". So it must be **the morning window and only that** — the same
 * `isWithinActiveWindow` the app's own `useGeolocation` gate uses, about a hundred minutes wide.
 *
 * Not the ride-home window. That one runs 11:00 to 22:00 because it decides whether Pickup Mode is
 * *eligible*, not whether anyone is travelling, and using it here held the radio for eleven hours a
 * day. The afternoon needs no window: the ride home starts when the rider opens the app to see
 * which train, and opening it is what moves the phase off `arrived` and turns location back on.
 */
export function nativeLocationActive(phase: RiderPhase, pickupActive: boolean, withinMorningWindow: boolean): boolean {
	return withinMorningWindow || pickupActive || (phase !== "planning" && phase !== "arrived");
}

/**
 * When the card's journey is over, as opposed to the rider's day.
 *
 * Off the train ends it in either direction. The remaining morning shuttle to College Prep still
 * belongs in the app, but its estimate is not accurate enough to earn a Live Activity.
 *
 * This is the only lever the web has over the installed shell, which ends the activity for good on
 * `arrived` and nothing else (`ActivityLifecycle.nextActivityCommand`). It is a statement about the
 * card rather than about him, the same way the pickup already reports `picked_up` as `arrived`.
 */
export function journeyFinished(phase: RiderPhase | null): boolean {
	return phase === "arrived" || phase === "at_destination";
}

/**
 * The journey as the shell should show it.
 *
 * Built by calling `cardStatus` — the same function the hero card renders — rather than writing a
 * second description of the same moment. Two places wording one thing differently is how a rider
 * learns to trust neither.
 *
 * What the card cannot give is the *instant* to count to: its hero is an already-computed number of
 * minutes, and the wire carries a moment so the system can tick it down without being updated. That
 * is the one thing worked out here.
 */
const METERS_PER_MILE = 1609.344;

/** The same figure the deck's last card shows, so the lock screen and the app agree. */
function milesAway(meters: number): string {
	const miles = meters / METERS_PER_MILE;
	return `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} ${miles >= 0.95 && miles < 1.05 ? "mile" : "miles"}`;
}

export function nativeJourneyFrom(input: NativeJourneyInput): NativeJourney {	const { plan, mode, finalLabel, nowMs, timezone } = input;
	const base = {
		phase: input.phase,
		direction: input.direction,
		...(input.activityKey ? { activityKey: input.activityKey } : {}),
		...(input.reopenActivity ? { reopenActivity: true } : {}),
		locationActive: input.locationActive,
		dayKey: serviceDateKey(nowMs, timezone),
		atMs: nowMs,
		glyph: glyphFor(mode),
		value: null,
	};
	if (input.presentation) {
		return { ...base, ...input.presentation };
	}

	if (mode === "done") {
		return { ...base, lead: "ARRIVED", leadShort: "ARRIVED", detail: finalLabel, note: null, tone: "good", countdownToMs: null };
	}

	/*
	 * Off the train and walking. `cardStatus` has no branch for this — the deck hands it to
	 * `onwardStatus` instead — so falling through would answer with the leave-the-house verbs.
	 *
	 * Above the missing-plan guard on purpose: his train has arrived by now and dropped out of the
	 * options, and "NO TRAIN" is not what the last half mile is about.
	 */
	if (mode === "offboard") {
		const leg = input.onward ?? null;
		const meters = leg?.roadMeters ?? leg?.metersAway ?? null;
		const label = leg?.label ?? finalLabel;
		const arrivalMs = leg?.seconds != null ? nowMs + leg.seconds * 1000 : (plan?.finalArrivalMs ?? null);
		return {
			...base,
			lead: label ? `${label.toUpperCase()} in` : "ARRIVING in",
			leadShort: label ? `${shortLabel(label)} in` : "ARRIVING",
			detail: meters == null ? null : `${milesAway(meters)} away`,
			note: null,
			tone: "good",
			countdownToMs: arrivalMs,
		};
	}

	/*
	 * Nothing to count, and nothing under way.
	 *
	 * `planning` is the shell's word for "there should be no card" (`worthShowing`), and it is also
	 * what keeps `useJourneyPin` from opening a pin. A NO TRAIN card has now been reported as a
	 * fault three times: at 08:14 on 2026-08-31, on the last walk to school, and on 2026-09-09 when
	 * the driver's lock screen carried one for thirty-two minutes after the pickup was over. It is
	 * also what every cold start raises for the second before its snapshot arrives.
	 *
	 * Once a leg has actually started the card stays, badly: at that point NO TRAIN is news.
	 */
	if (!plan) {
		const started = input.phase !== "at_home" && input.phase !== "planning";
		return {
			...base,
			phase: started ? input.phase : "planning",
			lead: "NO TRAIN",
			leadShort: "NO TRAIN",
			detail: null,
			note: null,
			tone: "bad",
			countdownToMs: null,
		};
	}

	/*
	 * The drive is the one place this deliberately says more than the card.
	 *
	 * The card's hero is TIME TO SPARE, which is a margin with no moment to count to. Driving, the
	 * two things worth knowing are when he reaches the station and how much slack that leaves, so
	 * both are here: the station is the countdown, the spare rides beside it.
	 */
	if (mode === "drive" && plan.stationArrivalMs != null) {
		const spare = Math.round((plan.boarding.predictedMs - plan.stationArrivalMs) / 60_000);
		const buffer = Math.round(plan.stationBufferSeconds / 60);
		return {
			...base,
			lead: input.boardingLabel ? `${input.boardingLabel.toUpperCase()} in` : "DROP OFF in",
			leadShort: input.boardingLabel ? `${shortLabel(input.boardingLabel)} in` : "DROP OFF",
			detail: `${clock(plan.boarding.predictedMs, timezone)} train`,
			note: spare < 0 ? `${-spare} min late` : `${spare} min spare`,
			tone: spare >= buffer ? "good" : spare >= Math.max(2, Math.round(buffer / 2)) ? "warn" : "bad",
			countdownToMs: plan.stationArrivalMs,
		};
	}

	const status = input.cardOverride ?? cardStatus(plan, input.hasAccessLeg, nowMs, timezone, mode, input.destinationIndex);
	const named = input.cardOverride ? null : naming(input, plan);
	return {
		...base,
		// A verb card — LEAVE NOW, MISSED — puts its words in the hero and leaves the lead empty.
		lead: named ? `${stationLabel(named.station).toUpperCase()} ${named.verb}` : (status.lead ?? status.hero),
		leadShort: named ? `${shortLabel(named.station)} ${named.verb}` : (status.lead ?? status.hero),
		detail: status.sub || null,
		note: status.note ? `${status.note.value} ${status.note.label}` : null,
		tone: (status.tone ?? "") as NativeTone,
		countdownToMs: input.cardOverride ? null : countdownTarget(input, plan),
	};
}

/**
 * Which station the number is about, and what the number means.
 *
 * Both name where he is *going*, not where he is standing: waiting at Civic Center the useful thing
 * is which train this is, and it is the Rockridge one. What differs is the moment being counted —
 * on a platform it is the train reaching him, aboard it is him reaching the stop. "ROCKRIDGE train
 * in" said from a seat on that train is describing something that has already happened.
 *
 * Null where the card's own verb is the better line: a departed train, and every mode that is not
 * about a train at all.
 */
function naming(input: NativeJourneyInput, plan: CommutePlan): { station: string; verb: "train in" | "in" } | null {
	if (input.mode !== "platform" && input.mode !== "onboard") {
		return null;
	}
	const arrival = plan.destinations[input.destinationIndex] ?? plan.destinations[0] ?? null;
	if (!arrival) {
		return null;
	}
	if (input.mode === "onboard") {
		return { station: arrival.stopName, verb: "in" };
	}
	// Which station it left from is not the news once it has gone.
	return plan.urgency === "departed" ? null : { station: arrival.stopName, verb: "train in" };
}

/**
 * Which leg the number is about.
 *
 * Off the train is a drive because it is one: the shuttle down the hill in the morning, David at
 * the kerb in the afternoon. Arriving has no activity left to draw a glyph on.
 */
function glyphFor(mode: CardQuestion): NativeGlyph {
	switch (mode) {
		case "drive":
		case "offboard":
			return "drive";
		case "platform":
			return "wait";
		case "onboard":
			return "train";
		case "done":
			return "";
		default:
			return "leave";
	}
}

/** The moment the card's number is about, which the card itself has already turned into minutes. */
function countdownTarget(input: NativeJourneyInput, plan: CommutePlan): number | null {
	switch (input.mode) {
		case "onboard": {
			const arrival = plan.destinations[input.destinationIndex] ?? plan.destinations[0] ?? null;
			return arrival?.predictedMs ?? null;
		}
		case "offboard":
			return plan.finalArrivalMs;
		case "platform":
		case "drive":
			return plan.boarding.predictedMs;
		default:
			/*
			 * With no drive there is nothing to leave for, so the card counts the train in — and the
			 * island has to count the same thing. On 2026-09-09 at 15:30 Eben's app read "TRAIN IN 10,
			 * 3:40" while his island counted five minutes, because the words came from `cardStatus`
			 * and the timer came from his walk-out time. Two right answers to different questions.
			 *
			 * `startBy` rather than `leaveBy` where there is a drive: the out-the-door buffer is part
			 * of what the card counts.
			 */
			return input.hasAccessLeg ? (plan.startByMs ?? plan.leaveByMs) : plan.boarding.predictedMs;
	}
}
